import React, { createContext, useContext, useReducer, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { debounce, throttle, memoize } from '../utils/performance.js';
import { log } from '../utils/environment.js';
// Initial state
const initialState = {
  pages: [],
  currentPageIndex: 0,
  selectedElement: null,
  selectedElements: [],
  selectedTextRange: null,
  history: {
    past: [],
    present: null,
    future: []
  },
  design: {
    fontFamily: 'Inter, system-ui',
    fontSize: 14,
    lineHeight: 1.6,
    letterSpacing: 'normal',
    accentColor: '#1ca29b',
    backgroundColor: '#ffffff',
    textColor: '#2d3748',
    padding: '40px',
    margin: '0px'
  },
  zoom: 100,
  isPreviewMode: false,
  isLoading: false,
  error: null,
  currentTemplateId: null,
  hasLoadedInitialTemplate: false,
  appliedDocumentStyles: {}
};

// Action types
const ActionTypes = {
  ADD_PAGE: 'ADD_PAGE',
  DELETE_PAGE: 'DELETE_PAGE',
  DUPLICATE_PAGE: 'DUPLICATE_PAGE',
  SELECT_PAGE: 'SELECT_PAGE',
  UPDATE_PAGE_CONTENT: 'UPDATE_PAGE_CONTENT',
  SELECT_ELEMENT: 'SELECT_ELEMENT',
  UPDATE_ELEMENT_STYLE: 'UPDATE_ELEMENT_STYLE',
  UPDATE_DESIGN: 'UPDATE_DESIGN',
  SET_ZOOM: 'SET_ZOOM',
  TOGGLE_PREVIEW_MODE: 'TOGGLE_PREVIEW_MODE',
  LOAD_TEMPLATE: 'LOAD_TEMPLATE',
  SET_HAS_LOADED: 'SET_HAS_LOADED',
  UNDO: 'UNDO',
  REDO: 'REDO',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  DELETE_ELEMENT: 'DELETE_ELEMENT',
  DUPLICATE_ELEMENT: 'DUPLICATE_ELEMENT',
  UPDATE_ELEMENT: 'UPDATE_ELEMENT',
  RESET_SELECTION: 'RESET_SELECTION',
  SET_APPLIED_STYLES: 'SET_APPLIED_STYLES'
};

// Reducer function with optimizations
const editorReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.ADD_PAGE: {
      const newPage = {
        id: Date.now(),
        title: `Page ${state.pages.length + 1}`,
        content: '<div style="padding: 40px; text-align: center;"><h2>New Page</h2><p>Add your content here...</p></div>',
        elements: []
      };
      const newPages = [...state.pages, newPage];
      return {
        ...state,
        pages: newPages,
        currentPageIndex: newPages.length - 1,
        selectedElement: null,
        selectedElements: [],
        history: {
          past: [...state.history.past, { pages: state.pages, selectedElement: state.selectedElement }],
          present: { pages: newPages, selectedElement: null },
          future: []
        }
      };
    }

    case ActionTypes.DELETE_PAGE: {
      if (state.pages.length <= 1) return state;
      const newPages = state.pages.filter((_, index) => index !== action.payload);
      const newIndex = state.currentPageIndex >= action.payload 
        ? Math.max(0, state.currentPageIndex - 1)
        : state.currentPageIndex;
      return {
        ...state,
        pages: newPages,
        currentPageIndex: newIndex,
        selectedElement: null,
        selectedElements: [],
        history: {
          past: [...state.history.past, { pages: state.pages, selectedElement: state.selectedElement }],
          present: { pages: newPages, selectedElement: null },
          future: []
        }
      };
    }

    case ActionTypes.DUPLICATE_PAGE: {
      const pageToDuplicate = { 
        ...state.pages[action.payload], 
        id: Date.now(),
        title: `${state.pages[action.payload].title} (Copy)`
      };
      const newPages = [...state.pages];
      newPages.splice(action.payload + 1, 0, pageToDuplicate);
      return {
        ...state,
        pages: newPages,
        currentPageIndex: action.payload + 1,
        selectedElement: null,
        selectedElements: [],
        history: {
          past: [...state.history.past, { pages: state.pages, selectedElement: state.selectedElement }],
          present: { pages: newPages, selectedElement: null },
          future: []
        }
      };
    }

    case ActionTypes.SELECT_PAGE:
      if (state.currentPageIndex === action.payload) return state;
      return {
        ...state,
        currentPageIndex: action.payload,
        selectedElement: null,
        selectedElements: []
      };

    case ActionTypes.UPDATE_PAGE_CONTENT: {
      const newPages = [...state.pages];
      const currentIndex = state.currentPageIndex;
      
      // Only update if content actually changed
      if (newPages[currentIndex].content === action.payload.content) {
        return state;
      }
      
      newPages[currentIndex] = {
        ...newPages[currentIndex],
        content: action.payload.content,
        lastModified: Date.now()
      };
      return {
        ...state,
        pages: newPages,
        history: {
          past: [...state.history.past, { 
            pages: state.pages, 
            selectedElement: state.selectedElement 
          }],
          present: { 
            pages: newPages, 
            selectedElement: state.selectedElement 
          },
          future: []
        }
      };
    }

    case ActionTypes.SELECT_ELEMENT:
      if (state.selectedElement?.element === action.payload?.element) {
        return state;
      }
      return {
        ...state,
        selectedElement: action.payload,
        selectedElements: action.payload ? [action.payload] : []
      };

    case ActionTypes.UPDATE_DESIGN: {
      const changes = action.payload;
      const hasChanges = Object.keys(changes).some(key => 
        state.design[key] !== changes[key]
      );
      
      if (!hasChanges) return state;
      
      return {
        ...state,
        design: { ...state.design, ...changes },
        appliedDocumentStyles: { ...state.appliedDocumentStyles, ...changes },
        history: {
          past: [...state.history.past, { 
            pages: state.pages, 
            design: state.design,
            selectedElement: state.selectedElement 
          }],
          present: { 
            pages: state.pages, 
            design: { ...state.design, ...changes },
            selectedElement: state.selectedElement 
          },
          future: []
        }
      };
    }

    case ActionTypes.SET_ZOOM:
      if (state.zoom === action.payload) return state;
      return {
        ...state,
        zoom: action.payload
      };

    case ActionTypes.TOGGLE_PREVIEW_MODE:
      return {
        ...state,
        isPreviewMode: !state.isPreviewMode,
        selectedElement: null,
        selectedElements: []
      };

    case ActionTypes.LOAD_TEMPLATE: {
      const template = action.payload;
      
      const newPages = template.pages || [{
        id: Date.now(),
        title: template.name || 'Template',
        content: template.content || '<div>Empty template</div>',
        elements: []
      }];
      
      return {
        ...state,
        pages: newPages,
        currentPageIndex: 0,
        design: { ...state.design, ...(template.design || {}) },
        appliedDocumentStyles: template.design || {},
        selectedElement: null,
        selectedElements: [],
        currentTemplateId: template.id,
        hasLoadedInitialTemplate: true,
        history: {
          past: [],
          present: { pages: newPages, selectedElement: null },
          future: []
        }
      };
    }

    case ActionTypes.SET_HAS_LOADED:
      return {
        ...state,
        hasLoadedInitialTemplate: action.payload
      };

    case ActionTypes.UNDO: {
      if (state.history.past.length === 0) return state;
      const previous = state.history.past[state.history.past.length - 1];
      const newPast = state.history.past.slice(0, -1);
      return {
        ...state,
        pages: previous.pages || state.pages,
        design: previous.design || state.design,
        selectedElement: previous.selectedElement || null,
        selectedElements: previous.selectedElement ? [previous.selectedElement] : [],
        history: {
          past: newPast,
          present: state.history.present,
          future: [{ 
            pages: state.history.present.pages, 
            design: state.design,
            selectedElement: state.selectedElement 
          }, ...state.history.future]
        }
      };
    }

    case ActionTypes.REDO: {
      if (state.history.future.length === 0) return state;
      const next = state.history.future[0];
      const newFuture = state.history.future.slice(1);
      return {
        ...state,
        pages: next.pages || state.pages,
        design: next.design || state.design,
        selectedElement: next.selectedElement || null,
        selectedElements: next.selectedElement ? [next.selectedElement] : [],
        history: {
          past: [...state.history.past, { 
            pages: state.history.present.pages, 
            design: state.design,
            selectedElement: state.selectedElement 
          }],
          present: next,
          future: newFuture
        }
      };
    }

    case ActionTypes.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };

    case ActionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload
      };

    case ActionTypes.RESET_SELECTION:
      return {
        ...state,
        selectedElement: null,
        selectedElements: []
      };

    default:
      return state;
  }
};

// Memoized selectors
const selectors = {
  getCurrentPage: memoize((pages, currentPageIndex) => {
    return pages[currentPageIndex] || null;
  }),
  
  getPageCount: memoize((pages) => {
    return pages.length;
  }),
  
  canUndo: memoize((history) => {
    return history.past.length > 0;
  }),
  
  canRedo: memoize((history) => {
    return history.future.length > 0;
  }),
};

// Create context
const EditorContext = createContext(null);

// Provider component with performance optimizations
export const EditorProvider = ({ children }) => {
  const [state, dispatch] = useReducer(editorReducer, initialState);

  // Memoized actions
  const addPage = useCallback(() => {
    dispatch({ type: ActionTypes.ADD_PAGE });
  }, []);

  const deletePage = useCallback((index) => {
    dispatch({ type: ActionTypes.DELETE_PAGE, payload: index });
  }, []);

  const duplicatePage = useCallback((index) => {
    dispatch({ type: ActionTypes.DUPLICATE_PAGE, payload: index });
  }, []);

  const selectPage = useCallback((index) => {
    dispatch({ type: ActionTypes.SELECT_PAGE, payload: index });
  }, []);

  // Debounced page content update
  const updatePageContent = useMemo(() => 
    debounce((content) => {
      dispatch({ 
        type: ActionTypes.UPDATE_PAGE_CONTENT, 
        payload: { content } 
      });
    }, 300),
    []
  );

  const selectElement = useCallback((element) => {
    dispatch({ type: ActionTypes.SELECT_ELEMENT, payload: element });
  }, []);

  // Throttled design updates for better performance
  const updateDesign = useMemo(() => 
    throttle((designUpdates) => {
      dispatch({ type: ActionTypes.UPDATE_DESIGN, payload: designUpdates });
    }, 100),
    []
  );

  const setAppliedStyles = useCallback((styles) => {
    dispatch({ type: ActionTypes.SET_APPLIED_STYLES, payload: styles });
  }, []);

  const setZoom = useCallback((zoom) => {
    dispatch({ type: ActionTypes.SET_ZOOM, payload: zoom });
  }, []);

  const togglePreviewMode = useCallback(() => {
    dispatch({ type: ActionTypes.TOGGLE_PREVIEW_MODE });
  }, []);

  const loadTemplate = useCallback((template) => {
    log.info('Loading template:', template?.name);
    dispatch({ type: ActionTypes.LOAD_TEMPLATE, payload: template });
  }, []);

  const setHasLoaded = useCallback((hasLoaded) => {
    dispatch({ type: ActionTypes.SET_HAS_LOADED, payload: hasLoaded });
  }, []);

  const undo = useCallback(() => {
    dispatch({ type: ActionTypes.UNDO });
  }, []);

  const redo = useCallback(() => {
    dispatch({ type: ActionTypes.REDO });
  }, []);

  const setLoading = useCallback((loading) => {
    dispatch({ type: ActionTypes.SET_LOADING, payload: loading });
  }, []);

  const setError = useCallback((error) => {
    log.error('Editor error:', error);
    dispatch({ type: ActionTypes.SET_ERROR, payload: error });
  }, []);

  const resetSelection = useCallback(() => {
    dispatch({ type: ActionTypes.RESET_SELECTION });
  }, []);

  // Memoized derived state
  const currentPage = useMemo(() => 
    selectors.getCurrentPage(state.pages, state.currentPageIndex),
    [state.pages, state.currentPageIndex]
  );

  const pageCount = useMemo(() => 
    selectors.getPageCount(state.pages),
    [state.pages]
  );

  const canUndo = useMemo(() => 
    selectors.canUndo(state.history),
    [state.history]
  );

  const canRedo = useMemo(() => 
    selectors.canRedo(state.history),
    [state.history]
  );

  // Context value
  const value = useMemo(() => ({
    state,
    actions: {
      addPage,
      deletePage,
      duplicatePage,
      selectPage,
      updatePageContent,
      selectElement,
      updateDesign,
      setAppliedStyles,
      setZoom,
      togglePreviewMode,
      loadTemplate,
      setHasLoaded,
      undo,
      redo,
      setLoading,
      setError,
      resetSelection
    },
    currentPage,
    pageCount,
    canUndo,
    canRedo
  }), [
    state,
    addPage,
    deletePage,
    duplicatePage,
    selectPage,
    updatePageContent,
    selectElement,
    updateDesign,
    setAppliedStyles,
    setZoom,
    togglePreviewMode,
    loadTemplate,
    setHasLoaded,
    undo,
    redo,
    setLoading,
    setError,
    resetSelection,
    currentPage,
    pageCount,
    canUndo,
    canRedo
  ]);

  // Performance monitoring
  useEffect(() => {
    if (state.pages.length > 0) {
      log.debug(`Editor state: ${state.pages.length} pages, zoom: ${state.zoom}%`);
    }
  }, [state.pages.length, state.zoom]);

  return (
    <EditorContext.Provider value={value}>
      {children}
    </EditorContext.Provider>
  );
};

EditorProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Custom hook
export const useEditor = () => {
  const context = useContext(EditorContext);
  if (!context) {
    throw new Error('useEditor must be used within an EditorProvider');
  }
  return context;
};

// Export selectors for testing
export { selectors };